import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { CircleGauge, Download, Plus } from 'lucide-react';
import ROUTE from 'src/utils/routeConstants';
import { USER_ROLES } from 'src/utils/constants';
import useAuth from 'src/context/AuthContext';
import { useTheme as useAppTheme } from 'src/context/ThemeContext';
import { setErrorInfo } from 'src/redux/modalSlice';
import { getAllVehicleGroupList } from 'src/redux/groupSlice';
import { getAllSites } from 'src/redux/siteSlice';
import { isAdminUser } from 'src/utils/getInitials';
import { isTyreManagementV2Enabled } from 'src/utils/tyreManagementFeature';
import { isFleetOnlyModeEnabled } from 'src/utils/appMode';
import { Button } from 'src/features/tyre-management/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/features/tyre-management/components/ui/card';
import { Skeleton } from 'src/features/tyre-management/components/ui/skeleton';
import EmptyState from 'src/features/tyre-management/components/EmptyState';
import ModuleHeader from 'src/features/tyre-management/components/ModuleHeader';
import { TableSkeleton } from 'src/features/tyre-management/components/TmSkeletonLayouts';
import { vehicleListApi } from './api/vehicleListApi';
import { toRequestParams, hasActiveConstraints } from './hooks/vehicleListQuery';
import useVehicleListQuery from './hooks/useVehicleListQuery';
import useVehicleListData from './hooks/useVehicleListData';
import QueryWorkbench from './components/QueryWorkbench';
import AppliedFilters from './components/AppliedFilters';
import FleetStrip from './components/FleetStrip';
import VehicleDataTable, {
  ColumnVisibilityMenu,
  useHiddenColumns,
} from './components/VehicleDataTable';
import VehicleListPagination from './components/VehicleListPagination';
import ExportDialog from './components/ExportDialog';
import 'src/features/tyre-management/tyre-management.css';
import './vehicle-list.css';

const asList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const VehicleListPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { mode } = useAppTheme();
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = isAdminUser(getUser);
  const canCreate = user?.role === USER_ROLES.admin || user?.role === USER_ROLES.siteManager;
  const { allVehicleGroups } = useSelector((state) => state.groups);
  const { allSites } = useSelector((state) => state.sites);
  const appSettings = useSelector((state) => state.settings.appSettings);
  const tyreFeatureEnabled = isTyreManagementV2Enabled(appSettings);
  const fleetOnlyMode = isFleetOnlyModeEnabled(appSettings);
  const globalEnabled = tyreFeatureEnabled && !fleetOnlyMode;
  const canUseV2Editor = appSettings?.vehicle_details_v2_enabled === true;
  const { query, setQuery, clearAll } = useVehicleListQuery();
  const { hidden, toggle } = useHiddenColumns();
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const {
    rows, count, aggregates, loading, updating, error,
  } = useVehicleListData(query, refreshKey);

  const siteOptions = useMemo(() => asList(allSites), [allSites]);
  const groupOptions = useMemo(() => asList(allVehicleGroups), [allVehicleGroups]);

  useEffect(() => {
    dispatch(getAllVehicleGroupList());
    if (isAdmin) dispatch(getAllSites());
  }, [dispatch, isAdmin]);

  const siteName = useMemo(
    () => siteOptions.find((item) => String(item.id) === String(query.site))?.name,
    [siteOptions, query.site]
  );
  const groupName = useMemo(
    () => groupOptions.find((item) => String(item.id) === String(query.group))?.name,
    [groupOptions, query.group]
  );

  const handleSearch = useCallback((search) => setQuery({ search }), [setQuery]);
  const handleApplyFilters = useCallback((filters) => setQuery(filters), [setQuery]);
  const handleRemove = (key) => {
    if (key === 'search') setQuery({ search: '' });
    else if (key === 'active' || key === 'tyre_management_enabled') setQuery({ [key]: null });
    else setQuery({ [key]: '' });
  };

  const exportVehicles = async () => {
    setExporting(true);
    try {
      const blob = await vehicleListApi.exportList(toRequestParams(query));
      const exportSiteName = (siteName || 'all-sites')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      saveAs(
        blob,
        `retrack-vehicle-register-${exportSiteName || 'all-sites'}-${moment().format('YYYY-MM-DD_HHmm')}.xlsx`,
      );
      setExportOpen(false);
    } catch (requestError) {
      dispatch(setErrorInfo({ errorMsg: requestError.message || 'Unable to export vehicles.' }));
    } finally {
      setExporting(false);
    }
  };

  const constrained = hasActiveConstraints(query);
  const empty = !loading && !error && rows.length === 0;
  const showTable = !error && rows.length > 0;

  return (
    <main
      className="tyre-management-v2 vehicle-list-v2 min-h-[calc(100vh-64px)] px-4 py-5 sm:px-6 lg:px-8"
      data-theme={mode}
    >
      <Helmet>
        <title>Vehicles | Retrack FMS</title>
      </Helmet>
      <div className="w-full space-y-6">
        <ModuleHeader
          eyebrow="Fleet register"
          title="Vehicles"
          description="Find vehicles by registration, model, or asset code."
          actions={(
            <>
              {globalEnabled && (
                <Button type="button" variant="outline" onClick={() => history.push(ROUTE.tyreV2Overview)}>
                  <CircleGauge />
                  Tyre Management
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setExportOpen(true)}>
                <Download />
                Export
              </Button>
              {canCreate && (
                <Button type="button" onClick={() => history.push(ROUTE.vehicleCreate)}>
                  <Plus />
                  Add Vehicle
                </Button>
              )}
            </>
          )}
        />

        {!globalEnabled && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="status">
            {fleetOnlyMode
              ? 'Tyre tracking is unavailable in the current application mode.'
              : 'Tyre tracking is off company-wide, so its workspace is unavailable.'}
            {' '}Each vehicle’s saved tracking choice is unchanged.
          </p>
        )}

        <FleetStrip
          aggregates={aggregates}
          loading={loading}
          globalEnabled={globalEnabled}
          onOpenUnconfigured={isAdmin ? () => history.push(ROUTE.tyreV2Setup) : undefined}
        />

        <div aria-live="polite" className="sr-only">
          {updating ? 'Updating vehicle list' : `${count} matching vehicles`}
        </div>

        <Card>
          <CardHeader className="flex-col gap-4 space-y-0">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle>Vehicle list</CardTitle>
                <p className="mt-1 text-sm text-slate-500">
                  {loading ? <Skeleton height={14} width={120} /> : `${count} vehicles`}
                  {updating && !loading ? ' · Updating…' : ''}
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
                <QueryWorkbench
                  query={query}
                  onSearch={handleSearch}
                  onApplyFilters={handleApplyFilters}
                  siteOptions={siteOptions}
                  groupOptions={groupOptions}
                  globalEnabled={globalEnabled}
                  disabled={loading && !rows.length}
                />
                <ColumnVisibilityMenu hidden={hidden} toggle={toggle} globalEnabled={globalEnabled} />
              </div>
            </div>
            <AppliedFilters
              query={query}
              siteName={siteName}
              groupName={groupName}
              globalEnabled={globalEnabled}
              onRemove={handleRemove}
              onClear={clearAll}
            />
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {error && (
              <div className="px-6 pb-6">
                <EmptyState
                  title="Vehicle list unavailable"
                  description={error}
                  action={<Button type="button" onClick={() => setRefreshKey((current) => current + 1)}>Retry</Button>}
                />
              </div>
            )}
            {loading && !error && !rows.length && <TableSkeleton rows={8} columns={7} />}
            {empty && !constrained && (
              <div className="px-6 pb-6">
                <EmptyState
                  title="No vehicles yet"
                  description="Add the first vehicle to start the fleet register."
                  action={canCreate ? <Button type="button" onClick={() => history.push(ROUTE.vehicleCreate)}>Add Vehicle</Button> : null}
                />
              </div>
            )}
            {empty && constrained && (
              <div className="px-6 pb-6">
                <EmptyState
                  title="No vehicles match"
                  description="The current search and filters returned no vehicles."
                  action={<Button type="button" variant="outline" onClick={clearAll}>Clear filters</Button>}
                />
              </div>
            )}
            {showTable && (
              <>
                <VehicleDataTable
                  rows={rows}
                  ordering={query.ordering}
                  onSort={(ordering) => setQuery({ ordering })}
                  onOpen={(vehicleId) => history.push(`${ROUTE.vehicles}/${vehicleId}`)}
                  onOpenTyres={(vehicleId) => history.push(`${ROUTE.tyreV2Vehicles}?vehicle=${vehicleId}`)}
                  onEnableTyres={canCreate && canUseV2Editor
                    ? (vehicleId) => history.push(`${ROUTE.vehicles}/${vehicleId}/edit#edit-modules`)
                    : undefined}
                  hidden={hidden}
                  globalEnabled={globalEnabled}
                  updating={updating}
                />
                <VehicleListPagination
                  page={query.page}
                  pageSize={query.page_size}
                  count={count}
                  onPageChange={(page) => setQuery({ page })}
                  onPageSizeChange={(pageSize) => setQuery({ page_size: pageSize })}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <ExportDialog
        open={exportOpen}
        count={count}
        exporting={exporting}
        onClose={() => setExportOpen(false)}
        onConfirm={exportVehicles}
      />
    </main>
  );
};

export default VehicleListPage;
